import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { TimeEntry } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { DataState } from '../../components/ui/DataState.js';
import { formatDate } from '../../utils/date.js';
import { useTimeEntries } from '../time-entries/api.js';
import {
  BILLING_STATUS_FILTERS,
  billingStatusOf,
  matchesBillingStatusFilter,
} from '../time-entries/billing-status.js';

const HEAD_CLASS = 'px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500';
const CELL_CLASS = 'px-3 py-2 text-sm text-slate-700';

export function ProjectTimeEntriesTab({ projectId }: { projectId: string }): JSX.Element {
  const navigate = useNavigate();
  const query = useTimeEntries({
    page: 1,
    pageSize: 200,
    projectId,
    sortBy: 'workDate',
    sortDir: 'desc',
  });
  const rows = query.data?.data ?? [];

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((e) => {
      if (!matchesBillingStatusFilter(e, statusFilter)) return false;
      if (!term) return true;
      const status = billingStatusOf(e);
      const haystack = [
        formatDate(e.workDate),
        `${e.hours}`,
        e.memberName ?? '',
        e.ticketNumber != null ? `#${e.ticketNumber}` : '',
        e.ticketTitle ?? '',
        e.notes ?? '',
        status.label,
        status.detail ?? '',
        e.invoiceNumber ?? '',
        e.externalInvoiceRef ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [rows, search, statusFilter]);

  const totals = useMemo(() => {
    let total = 0;
    let billable = 0;
    for (const e of filtered) {
      const hours = Number(e.hours);
      if (!Number.isFinite(hours)) continue;
      total += hours;
      if (e.billable) billable += hours;
    }
    return { total, billable };
  }, [filtered]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-slate-800">Time entries</h2>
        {filtered.length > 0 ? (
          <p className="text-sm text-slate-500">
            {filtered.length} entr{filtered.length === 1 ? 'y' : 'ies'} ·{' '}
            {totals.total.toFixed(2)} hours ({totals.billable.toFixed(2)} billable)
          </p>
        ) : null}
      </div>
      {rows.length > 0 ? (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by date, member, ticket, notes, status…"
            className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-slate-300 px-2.5 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            {BILLING_STATUS_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <table className="w-full border-collapse">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              {['Date', 'Member', 'Ticket', 'Hours', 'Status', 'Notes'].map((h) => (
                <th key={h} className={HEAD_CLASS}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={6}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              isEmpty={filtered.length === 0}
              emptyTitle={
                rows.length === 0
                  ? 'No time entries for this project.'
                  : 'No time entries match the filter.'
              }
              onRetry={() => void query.refetch()}
            >
              {filtered.map((e: TimeEntry) => {
                const status = billingStatusOf(e);
                return (
                  <tr
                    key={e.id}
                    onClick={() => navigate(`/time-entries/${e.id}`)}
                    className="cursor-pointer hover:bg-slate-50"
                  >
                    <td className={`${CELL_CLASS} text-slate-600`}>{formatDate(e.workDate)}</td>
                    <td className={`${CELL_CLASS} text-slate-600`}>{e.memberName ?? '—'}</td>
                    <td className={`${CELL_CLASS} text-slate-600`}>
                      {e.ticketNumber != null
                        ? `#${e.ticketNumber}${e.ticketTitle ? ` · ${e.ticketTitle}` : ''}`
                        : '—'}
                    </td>
                    <td className={`${CELL_CLASS} tabular-nums`}>{e.hours}</td>
                    <td className={CELL_CLASS}>
                      <Badge tone={status.tone}>
                        {status.detail ? `${status.label} · ${status.detail}` : status.label}
                      </Badge>
                    </td>
                    <td className={`${CELL_CLASS} text-slate-500`}>{e.notes ?? '—'}</td>
                  </tr>
                );
              })}
            </DataState>
          </tbody>
        </table>
      </div>
    </div>
  );
}
